import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Package,
  Calendar,
  Users,
  ChevronRight,
  RefreshCw
} from 'lucide-react'
import { useAuth } from '../App'
import { AgentAPI, Allocation } from '../services/api'
import Header from '../components/Header'
import BottomNav from '../components/BottomNav'

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [allocations, setAllocations] = useState<Allocation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAllocations = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await AgentAPI.getMyAllocations()
      setAllocations(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load allocations')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllocations()
  }, [])

  // Calculate stats
  const stats = {
    totalAllocated: allocations.reduce((sum, a) => sum + a.allocated_qty, 0),
    totalSold: allocations.reduce((sum, a) => sum + a.sold_qty, 0),
    totalAvailable: allocations.reduce((sum, a) => sum + a.available_qty, 0),
    activeAllocations: allocations.filter(a => a.status === 'active').length
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'expired': return 'bg-red-100 text-red-800'
      case 'returned': return 'bg-amber-100 text-amber-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Get payment status color
  const getPaymentColor = (status: string) => {
    switch (status) {
      case 'full': return 'text-green-600'
      case 'advance': return 'text-blue-600'
      case 'partial': return 'text-amber-600'
      case 'overdue': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-safe">
      <Header title="My Allocations" showLogout onLogout={logout} />

      <div className="p-4 space-y-4">
        {/* Welcome */}
        <div className="bg-primary-500 rounded-2xl p-4 text-white">
          <p className="text-primary-100 text-sm">Welcome back,</p>
          <h2 className="text-xl font-bold">{user?.name}</h2>
          <p className="text-primary-200 text-sm mt-1">{user?.agencyName}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-4">
            <p className="text-sm text-gray-500">Allocated</p>
            <p className="text-2xl font-bold text-gray-900">{stats.totalAllocated}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-sm text-gray-500">Sold</p>
            <p className="text-2xl font-bold text-green-600">{stats.totalSold}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-sm text-gray-500">Available</p>
            <p className="text-2xl font-bold text-blue-600">{stats.totalAvailable}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-sm text-gray-500">Active Series</p>
            <p className="text-2xl font-bold text-gray-900">{stats.activeAllocations}</p>
          </div>
        </div>

        {/* Refresh Button */}
        <button
          onClick={fetchAllocations}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2 text-primary-600 bg-primary-50 rounded-xl"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Allocations List */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900">Your Allocations</h3>
          
          {loading && allocations.length === 0 ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : allocations.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">No allocations yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Contact your operator for allocation
              </p>
            </div>
          ) : (
            allocations.map((alloc, index) => (
              <motion.div
                key={alloc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/allocation/${alloc.id}`)}
                className="bg-white rounded-xl p-4 active:bg-gray-50 cursor-pointer"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {alloc.series_name || `Series #${alloc.series_id}`}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {alloc.series_code || 'No code'}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(alloc.status)}`}>
                    {alloc.status}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>{alloc.departure_date ? new Date(alloc.departure_date).toLocaleDateString() : 'TBD'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users size={14} />
                    <span>{alloc.sold_qty} / {alloc.allocated_qty}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-xs text-gray-500">Available: </span>
                    <span className="font-medium text-green-600">{alloc.available_qty}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${getPaymentColor(alloc.payment_status)}`}>
                      {alloc.payment_status}
                    </span>
                    <ChevronRight size={18} className="text-gray-400" />
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      <BottomNav activeTab="home" />
    </div>
  )
}

export default Dashboard
